import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { medicalDocumentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft,
  Upload,
  FileText,
  Stethoscope,
  FlaskConical,
  Scan,
  Syringe,
  Pill,
  Loader2,
  CheckCircle,
  Image,
  File
} from 'lucide-react';
import { toast } from 'sonner';

const DOCUMENT_CATEGORIES = [
  { value: 'consultation', label: 'Compte-rendu de consultation', icon: Stethoscope, color: 'blue' },
  { value: 'prescription', label: 'Ordonnance', icon: Pill, color: 'green' },
  { value: 'lab_results', label: 'Analyses de laboratoire', icon: FlaskConical, color: 'purple' },
  { value: 'imaging', label: 'Imagerie medicale', icon: Scan, color: 'orange' },
  { value: 'vaccination', label: 'Carnet de vaccination', icon: Syringe, color: 'teal' },
  { value: 'other', label: 'Autre document', icon: FileText, color: 'slate' }
];

const MedicalRecordsUpload = () => {
  const navigate = useNavigate();
  
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    document_date: '',
    professional_name: ''
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas depasser 10 Mo');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Format accepte : JPG, PNG, WebP ou PDF');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error('Veuillez selectionner un fichier');
      return;
    }
    
    if (!formData.title.trim()) {
      toast.error('Veuillez donner un titre au document');
      return;
    }
    
    if (!formData.category) {
      toast.error('Veuillez choisir une categorie');
      return;
    }
    
    setUploading(true);
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', selectedFile);
      
      const params = {
        title: formData.title,
        category: formData.category
      };
      
      if (formData.description) params.description = formData.description;
      if (formData.document_date) params.document_date = formData.document_date;
      if (formData.professional_name) params.professional_name = formData.professional_name;
      
      await medicalDocumentsAPI.upload(uploadData, params);
      
      setUploadSuccess(true);
      toast.success('Document ajoute a votre dossier medical');
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/patient/medical-records');
      }, 2000);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setUploading(false);
    }
  };

  if (uploadSuccess) {
    return (
      <div className="max-w-lg mx-auto p-4 pt-8">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Document ajoute !</h2>
            <p className="text-slate-500 mb-4">
              Votre document a ete ajoute a votre dossier medical.
            </p>
            <p className="text-sm text-slate-400">Redirection en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto p-4 pb-24" data-testid="medical-records-upload-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/patient/medical-records')}
          className="rounded-full"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ajouter un document
          </h1>
          <p className="text-sm text-slate-500">Ajoutez un document a votre dossier medical</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File Upload Zone */}
        <Card className="border-2 border-dashed border-slate-200 hover:border-[#1976D2] transition-colors">
          <CardContent className="p-6">
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileSelect}
              data-testid="file-input"
            />
            
            {selectedFile ? (
              <div className="text-center">
                {preview ? (
                  <img
                    src={preview}
                    alt="Apercu"
                    className="max-h-48 mx-auto rounded-lg mb-4 object-contain"
                  />
                ) : (
                  <div className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <File className="w-10 h-10 text-slate-400" />
                  </div>
                )}
                <p className="font-medium text-slate-900">{selectedFile.name}</p>
                <p className="text-sm text-slate-500 mb-3">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} Mo
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreview(null);
                  }}
                >
                  Changer de fichier
                </Button>
              </div>
            ) : (
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center cursor-pointer py-8"
              >
                <div className="w-16 h-16 bg-[#1976D2]/10 rounded-full flex items-center justify-center mb-4">
                  <Upload className="w-8 h-8 text-[#1976D2]" />
                </div>
                <p className="font-medium text-slate-900 mb-1">
                  Cliquez pour selectionner un fichier
                </p>
                <p className="text-sm text-slate-500">
                  JPG, PNG, WebP ou PDF (max 10 Mo)
                </p>
              </label>
            )}
          </CardContent>
        </Card>

        {/* Document Details */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-5 space-y-4">
            {/* Title */}
            <div>
              <Label htmlFor="title" className="text-sm font-medium text-slate-700">
                Titre du document *
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Bilan sanguin janvier 2026"
                className="mt-1"
                data-testid="title-input"
              />
            </div>

            {/* Category */}
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Type de document *
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="mt-1" data-testid="category-select">
                  <SelectValue placeholder="Choisir une categorie" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span>{cat.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="document_date" className="text-sm font-medium text-slate-700">
                Date du document
              </Label>
              <Input
                id="document_date"
                type="date"
                value={formData.document_date}
                onChange={(e) => setFormData({ ...formData, document_date: e.target.value })}
                className="mt-1"
                data-testid="date-input"
              />
            </div>

            {/* Professional name */}
            <div>
              <Label htmlFor="professional_name" className="text-sm font-medium text-slate-700">
                Professionnel de sante
              </Label>
              <Input
                id="professional_name"
                value={formData.professional_name}
                onChange={(e) => setFormData({ ...formData, professional_name: e.target.value })}
                placeholder="Ex: Dr. Atangana"
                className="mt-1"
                data-testid="professional-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                Notes ou commentaires
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Informations supplementaires..."
                rows={3}
                className="mt-1 resize-none"
                data-testid="description-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={uploading || !selectedFile}
          className="w-full bg-[#E53935] hover:bg-[#C62828] h-12 rounded-xl text-base font-medium"
          data-testid="submit-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 mr-2" />
              Ajouter a mon dossier
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default MedicalRecordsUpload;
